import { useState, useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useIntegrationStatus } from "@/hooks/use-integration-status";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { 
  Search, 
  Building2,
  TrendingUp,
  TrendingDown,
  Package,
  Clock,
  CheckCircle,
  AlertTriangle,
  Download,
  Filter,
  RefreshCw,
  ExternalLink,
  Mail,
  Phone,
  MapPin,
  User,
  FileText,
  DollarSign,
  Calendar,
  Star,
  BarChart3,
  Truck,
  ShoppingCart,
  Globe,
  Plus,
  Zap
} from "lucide-react";
import { toast } from "sonner";
import { useNavigation } from "@/components/layout/app-layout";
import { HelpButton } from "@/components/help-button";
import { ContextualHelpDrawer } from "@/components/contextual-help-drawer";
import { useContextualHelp } from "@/hooks/use-contextual-help";
import { StaticMetricCardAlt } from "@/components/StaticMetricCardAlt";

// Types
interface SupplierContact {
  name: string;
  role: string;
  email: string;
  phone: string;
  isPrimary: boolean;
}

interface SupplierAddress {
  type: "billing" | "shipping";
  line1: string;
  line2?: string;
  city: string;
  county?: string;
  postcode: string;
  country: string;
}

interface SupplierPerformance {
  onTimeDeliveryRate: number; // Percentage
  qualityRating: number; // 1-5 stars
  averageLeadTimeDays: number;
  totalOrdersPlaced: number;
  totalOrderValue: number;
  lastOrderDate: Date;
}

interface PurchaseOrderSummary {
  id: string;
  poNumber: string;
  orderDate: Date;
  expectedDeliveryDate: Date;
  status: string;
  totalValue: number;
  currency: string;
}

interface Supplier {
  id: string;
  code: string;
  name: string;
  tradingName?: string;
  status: "active" | "inactive" | "on-hold";
  category: string;
  taxNumber?: string;
  companyRegistration?: string;
  website?: string;
  
  // Contact information
  contacts: SupplierContact[];
  addresses: SupplierAddress[];
  
  // Financial terms
  paymentTerms: string;
  creditLimit: number;
  currency: string;
  accountBalance: number; // Amount owed to supplier
  
  // Performance metrics
  performance: SupplierPerformance;
  
  // Recent purchase orders
  recentPurchaseOrders: PurchaseOrderSummary[];
  
  // Metadata
  syncedFrom: string;
  lastSyncDate: Date;
  createdDate: Date;
  notes?: string;
}

// Mock data
const MOCK_SUPPLIERS: Supplier[] = [
  {
    id: "1",
    code: "SUP-001",
    name: "Supplier A Ltd",
    tradingName: "Supplier A",
    status: "active",
    category: "Raw Materials",
    taxNumber: "GB123456789",
    companyRegistration: "12345678",
    website: "https://www.suppliera.com",
    contacts: [
      {
        name: "John Smith",
        role: "Account Manager",
        email: "john@suppliera.com",
        phone: "+44 20 1234 5678",
        isPrimary: true
      },
      {
        name: "Sarah Johnson",
        role: "Sales Representative",
        email: "sarah@suppliera.com",
        phone: "+44 20 1234 5679",
        isPrimary: false
      }
    ],
    addresses: [
      {
        type: "billing",
        line1: "100 Business Park",
        line2: "Unit 5",
        city: "London",
        county: "Greater London",
        postcode: "SW1A 1AA",
        country: "United Kingdom"
      },
      {
        type: "shipping",
        line1: "100 Business Park",
        line2: "Unit 5",
        city: "London",
        county: "Greater London",
        postcode: "SW1A 1AA",
        country: "United Kingdom"
      }
    ],
    paymentTerms: "Net 30 days",
    creditLimit: 50000.00,
    currency: "GBP",
    accountBalance: 15750.00,
    performance: {
      onTimeDeliveryRate: 95.5,
      qualityRating: 4.5,
      averageLeadTimeDays: 7,
      totalOrdersPlaced: 48,
      totalOrderValue: 245600.00,
      lastOrderDate: new Date("2024-01-22")
    },
    recentPurchaseOrders: [
      {
        id: "1",
        poNumber: "PO-2024-001",
        orderDate: new Date("2024-01-15"),
        expectedDeliveryDate: new Date("2024-02-15"),
        status: "partially-received",
        totalValue: 15750.00,
        currency: "GBP"
      },
      {
        id: "4",
        poNumber: "PO-2024-004",
        orderDate: new Date("2024-01-10"),
        expectedDeliveryDate: new Date("2024-01-20"),
        status: "fully-received",
        totalValue: 5400.00,
        currency: "GBP"
      }
    ],
    syncedFrom: "Access Financials",
    lastSyncDate: new Date("2024-01-25T10:30:00"),
    createdDate: new Date("2023-06-15"),
    notes: "Preferred supplier for raw materials. Excellent quality and reliability."
  },
  {
    id: "2",
    code: "SUP-002",
    name: "Supplier B Corp",
    status: "active",
    category: "Components",
    taxNumber: "GB987654321",
    companyRegistration: "87654321",
    website: "https://www.supplierb.com",
    contacts: [
      {
        name: "Emma Davis",
        role: "Sales Manager",
        email: "emma@supplierb.com",
        phone: "+44 20 9876 5432",
        isPrimary: true
      }
    ],
    addresses: [
      {
        type: "billing",
        line1: "250 Industrial Estate",
        city: "Manchester",
        postcode: "M1 1AA",
        country: "United Kingdom"
      },
      {
        type: "shipping",
        line1: "250 Industrial Estate",
        city: "Manchester",
        postcode: "M1 1AA",
        country: "United Kingdom"
      }
    ],
    paymentTerms: "Net 30 days",
    creditLimit: 30000.00,
    currency: "GBP",
    accountBalance: 8500.00,
    performance: {
      onTimeDeliveryRate: 78.5,
      qualityRating: 3.5,
      averageLeadTimeDays: 12,
      totalOrdersPlaced: 32,
      totalOrderValue: 156800.00,
      lastOrderDate: new Date("2024-01-20")
    },
    recentPurchaseOrders: [
      {
        id: "2",
        poNumber: "PO-2024-002",
        orderDate: new Date("2024-01-20"),
        expectedDeliveryDate: new Date("2024-01-25"),
        status: "overdue",
        totalValue: 8500.00,
        currency: "GBP"
      },
      {
        id: "6",
        poNumber: "PO-2023-999",
        orderDate: new Date("2023-12-15"),
        expectedDeliveryDate: new Date("2024-01-05"),
        status: "cancelled",
        totalValue: 3200.00,
        currency: "GBP"
      }
    ],
    syncedFrom: "Access Financials",
    lastSyncDate: new Date("2024-01-25T10:30:00"),
    createdDate: new Date("2023-08-20"),
    notes: "Delivery reliability issues. Monitor closely."
  },
  {
    id: "3",
    code: "SUP-003",
    name: "Global Supplies Co",
    tradingName: "Global Supplies",
    status: "active",
    category: "Equipment",
    taxNumber: "GB555123456",
    companyRegistration: "55512345",
    website: "https://www.globalsupplies.com",
    contacts: [
      {
        name: "David Chen",
        role: "International Sales",
        email: "david@globalsupplies.com",
        phone: "+44 20 5555 1234",
        isPrimary: true
      },
      {
        name: "Lisa Wong",
        role: "Customer Service",
        email: "lisa@globalsupplies.com",
        phone: "+44 20 5555 1235",
        isPrimary: false
      }
    ],
    addresses: [
      {
        type: "billing",
        line1: "Global House",
        line2: "Technology Park",
        city: "Birmingham",
        county: "West Midlands",
        postcode: "B1 1AA",
        country: "United Kingdom"
      },
      {
        type: "shipping",
        line1: "Warehouse 7",
        line2: "Distribution Centre",
        city: "Birmingham",
        county: "West Midlands",
        postcode: "B2 2BB",
        country: "United Kingdom"
      }
    ],
    paymentTerms: "Net 45 days",
    creditLimit: 100000.00,
    currency: "GBP",
    accountBalance: 25600.00,
    performance: {
      onTimeDeliveryRate: 92.0,
      qualityRating: 4.0,
      averageLeadTimeDays: 14,
      totalOrdersPlaced: 24,
      totalOrderValue: 456700.00,
      lastOrderDate: new Date("2024-01-22")
    },
    recentPurchaseOrders: [
      {
        id: "3",
        poNumber: "PO-2024-003",
        orderDate: new Date("2024-01-22"),
        expectedDeliveryDate: new Date("2024-02-28"),
        status: "open",
        totalValue: 25600.00,
        currency: "GBP"
      }
    ],
    syncedFrom: "Access Financials",
    lastSyncDate: new Date("2024-01-25T10:30:00"),
    createdDate: new Date("2023-04-10"),
    notes: "Large equipment orders. Good for bulk purchases."
  },
  {
    id: "4",
    code: "SUP-004",
    name: "Food Supplier Ltd",
    status: "active",
    category: "Food Products",
    taxNumber: "GB777888999",
    companyRegistration: "77788899",
    website: "https://www.foodsupplier.com",
    contacts: [
      {
        name: "Maria Garcia",
        role: "Account Manager",
        email: "maria@foodsupplier.com",
        phone: "+44 20 7777 8888",
        isPrimary: true
      }
    ],
    addresses: [
      {
        type: "billing",
        line1: "Fresh Foods House",
        city: "Manchester",
        postcode: "M1 1AA",
        country: "United Kingdom"
      },
      {
        type: "shipping",
        line1: "456 Cold Storage Facility",
        city: "Manchester",
        postcode: "M1 1AA",
        country: "United Kingdom"
      }
    ],
    paymentTerms: "Net 14 days",
    creditLimit: 25000.00,
    currency: "GBP",
    accountBalance: 12300.00,
    performance: {
      onTimeDeliveryRate: 98.5,
      qualityRating: 5.0,
      averageLeadTimeDays: 2,
      totalOrdersPlaced: 156,
      totalOrderValue: 678900.00,
      lastOrderDate: new Date("2024-01-25")
    },
    recentPurchaseOrders: [
      {
        id: "5",
        poNumber: "PO-2024-005",
        orderDate: new Date("2024-01-25"),
        expectedDeliveryDate: new Date("2024-02-05"),
        status: "open",
        totalValue: 12300.00,
        currency: "GBP"
      }
    ],
    syncedFrom: "Access Financials",
    lastSyncDate: new Date("2024-01-25T10:30:00"),
    createdDate: new Date("2023-01-05"),
    notes: "Excellent for fresh food products. Very reliable and fast delivery."
  },
  {
    id: "5",
    code: "SUP-005",
    name: "Packaging Solutions Inc",
    status: "active",
    category: "Packaging",
    taxNumber: "GB444555666",
    companyRegistration: "44455566",
    website: "https://www.packagingsolutions.com",
    contacts: [
      {
        name: "Robert Taylor",
        role: "Business Development",
        email: "robert@packagingsolutions.com",
        phone: "+44 20 4444 5555",
        isPrimary: true
      }
    ],
    addresses: [
      {
        type: "billing",
        line1: "Packaging House",
        line2: "Innovation Quarter",
        city: "Leeds",
        county: "West Yorkshire",
        postcode: "LS1 1AA",
        country: "United Kingdom"
      },
      {
        type: "shipping",
        line1: "Packaging House",
        line2: "Innovation Quarter",
        city: "Leeds",
        county: "West Yorkshire",
        postcode: "LS1 1AA",
        country: "United Kingdom"
      }
    ],
    paymentTerms: "Net 30 days",
    creditLimit: 40000.00,
    currency: "GBP",
    accountBalance: 0.00,
    performance: {
      onTimeDeliveryRate: 88.0,
      qualityRating: 4.0,
      averageLeadTimeDays: 10,
      totalOrdersPlaced: 36,
      totalOrderValue: 189400.00,
      lastOrderDate: new Date("2024-01-10")
    },
    recentPurchaseOrders: [],
    syncedFrom: "Access Financials",
    lastSyncDate: new Date("2024-01-25T10:30:00"),
    createdDate: new Date("2023-09-12"),
    notes: "Good quality packaging materials."
  },
  {
    id: "6",
    code: "SUP-006",
    name: "Tech Components Ltd",
    status: "on-hold",
    category: "Electronics",
    taxNumber: "GB222333444",
    companyRegistration: "22233344",
    contacts: [
      {
        name: "James Wilson",
        role: "Sales Director",
        email: "james@techcomponents.com",
        phone: "+44 20 2222 3333",
        isPrimary: true
      }
    ],
    addresses: [
      {
        type: "billing",
        line1: "Tech Park",
        city: "Reading",
        postcode: "RG1 1AA",
        country: "United Kingdom"
      },
      {
        type: "shipping",
        line1: "Tech Park",
        city: "Reading",
        postcode: "RG1 1AA",
        country: "United Kingdom"
      }
    ],
    paymentTerms: "Net 30 days",
    creditLimit: 20000.00,
    currency: "GBP",
    accountBalance: 0.00,
    performance: {
      onTimeDeliveryRate: 65.0,
      qualityRating: 2.5,
      averageLeadTimeDays: 21,
      totalOrdersPlaced: 18,
      totalOrderValue: 89500.00,
      lastOrderDate: new Date("2023-12-05")
    },
    recentPurchaseOrders: [],
    syncedFrom: "Access Financials",
    lastSyncDate: new Date("2024-01-25T10:30:00"),
    createdDate: new Date("2023-11-20"),
    notes: "On hold due to quality issues and late deliveries. Under review."
  },
  {
    id: "7",
    code: "SUP-007",
    name: "Office Supplies Direct",
    status: "inactive",
    category: "Office Supplies",
    taxNumber: "GB111222333",
    companyRegistration: "11122233",
    contacts: [
      {
        name: "Susan Brown",
        role: "Account Manager",
        email: "susan@officesupplies.com",
        phone: "+44 20 1111 2222",
        isPrimary: true
      }
    ],
    addresses: [
      {
        type: "billing",
        line1: "Office Park",
        city: "Bristol",
        postcode: "BS1 1AA",
        country: "United Kingdom"
      },
      {
        type: "shipping",
        line1: "Office Park",
        city: "Bristol",
        postcode: "BS1 1AA",
        country: "United Kingdom"
      }
    ],
    paymentTerms: "Net 30 days",
    creditLimit: 10000.00,
    currency: "GBP",
    accountBalance: 0.00,
    performance: {
      onTimeDeliveryRate: 85.0,
      qualityRating: 3.5,
      averageLeadTimeDays: 5,
      totalOrdersPlaced: 12,
      totalOrderValue: 23400.00,
      lastOrderDate: new Date("2023-10-15")
    },
    recentPurchaseOrders: [],
    syncedFrom: "Access Financials",
    lastSyncDate: new Date("2024-01-25T10:30:00"),
    createdDate: new Date("2023-07-01"),
    notes: "Inactive - switched to alternative supplier."
  }
];

export function SuppliersPage() {
  const { navigateTo } = useNavigation();
  const integrationStatus = useIntegrationStatus();
  const { isHelpOpen, helpContent, toggleHelp, setIsHelpOpen } = useContextualHelp("suppliers");
  
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("active");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
  const [showDetailDrawer, setShowDetailDrawer] = useState(false);
  const [showQuickAddDrawer, setShowQuickAddDrawer] = useState(false);

  // Calculate summary statistics
  const stats = useMemo(() => {
    const activeSuppliers = MOCK_SUPPLIERS.filter(s => s.status === "active").length;
    const totalSuppliers = MOCK_SUPPLIERS.length;
    const onHoldSuppliers = MOCK_SUPPLIERS.filter(s => s.status === "on-hold").length;
    const totalOutstanding = MOCK_SUPPLIERS.reduce((sum, s) => sum + s.accountBalance, 0);

    return { activeSuppliers, totalSuppliers, onHoldSuppliers, totalOutstanding };
  }, []);

  // Get unique categories for filter
  const categories = useMemo(() => {
    const uniqueCategories = Array.from(new Set(MOCK_SUPPLIERS.map(s => s.category)));
    return uniqueCategories.sort();
  }, []);

  // Filter data
  const filteredData = useMemo(() => {
    let filtered = MOCK_SUPPLIERS;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(supplier =>
        supplier.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        supplier.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        supplier.category.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by status
    if (statusFilter !== "all") {
      filtered = filtered.filter(supplier => supplier.status === statusFilter);
    }

    // Filter by category
    if (categoryFilter !== "all") {
      filtered = filtered.filter(supplier => supplier.category === categoryFilter);
    }

    return filtered;
  }, [searchTerm, statusFilter, categoryFilter]);

  // Format date
  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(date);
  };

  // Format date and time
  const formatDateTime = (date: Date) => {
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  // Format currency
  const formatCurrency = (amount: number, currency: string = "GBP") => {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: currency,
    }).format(amount);
  };

  // Get status badge
  const getStatusBadge = (status: string) => {
    switch (status) {
      case "active":
        return <Badge variant="success" accent="success">Active</Badge>;
      case "inactive":
        return <Badge variant="secondary">Inactive</Badge>;
      case "on-hold":
        return <Badge variant="destructive">On Hold</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  // Get performance badge
  const getPerformanceBadge = (rate: number) => {
    if (rate >= 95) {
      return <Badge variant="success" accent="success">Excellent</Badge>;
    } else if (rate >= 85) {
      return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">Good</Badge>;
    } else if (rate >= 75) {
      return <Badge variant="secondary" className="bg-yellow-50 text-yellow-700 border-yellow-200">Fair</Badge>;
    } else {
      return <Badge variant="destructive">Poor</Badge>;
    }
  };

  // Render star rating
  const renderStarRating = (rating: number) => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-4 w-4 ${
              star <= rating
                ? "fill-yellow-400 text-yellow-400"
                : "fill-gray-200 text-gray-200"
            }`}
          />
        ))}
        <span className="text-sm text-muted-foreground ml-1">({rating.toFixed(1)})</span>
      </div>
    );
  };

  // Handle row click
  const handleRowClick = (supplier: Supplier) => {
    setSelectedSupplier(supplier);
    setShowDetailDrawer(true);
  };

  // Handle sync
  const handleSync = () => {
    toast.promise(
      new Promise((resolve) => setTimeout(resolve, 1500)),
      {
        loading: "Syncing with Access Financials...",
        success: "Suppliers synced successfully",
        error: "Failed to sync suppliers",
      }
    );
  };

  // Get primary contact
  const getPrimaryContact = (supplier: Supplier) => {
    return supplier.contacts.find(c => c.isPrimary) || supplier.contacts[0];
  };

  // Get billing address
  const getBillingAddress = (supplier: Supplier) => {
    return supplier.addresses.find(a => a.type === "billing") || supplier.addresses[0];
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-3xl font-bold tracking-tight">Suppliers</h1>
              {integrationStatus.isFinancialsIntegrated && (
                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                  Synced from Access Financials
                </Badge>
              )}
            </div>
            <p className="text-muted-foreground mt-2">
              View and manage supplier information{integrationStatus.isFinancialsIntegrated ? " synced from your ERP system" : ""}
            </p>
          </div>
          <div className="flex gap-2">
            <HelpButton onClick={toggleHelp} />
            {/* <Button onClick={() => setShowQuickAddDrawer(true)} variant="default">
              <Plus className="h-4 w-4 mr-2" />
              Quick Add Supplier
            </Button> */}
            <Button onClick={handleSync} variant="outline">
              <RefreshCw className="h-4 w-4 mr-2" />
              Sync Now
            </Button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StaticMetricCardAlt
          title="Active Suppliers"
          value={stats.activeSuppliers.toString()}
          icon={Building2}
          variant="success"
          changeLabel={`of ${stats.totalSuppliers} total`}
        />
        <StaticMetricCardAlt
          title="On Hold"
          value={stats.onHoldSuppliers.toString()}
          icon={AlertTriangle}
          variant="error"
          changeLabel="Requires attention"
        />
        <StaticMetricCardAlt
          title="Total Outstanding"
          value={formatCurrency(stats.totalOutstanding)}
          icon={DollarSign}
          variant="info"
          changeLabel="Amount owed to suppliers"
        />
        <StaticMetricCardAlt
          title="Categories"
          value={categories.length.toString()}
          icon={Package}
          variant="info"
          changeLabel="Supplier categories"
        />
      </div>

      {/* Main Content */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Supplier Directory</CardTitle>
              <CardDescription>
                View supplier information synced from Access Financials
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm">
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Search and Filters */}
          <div className="flex flex-col sm:flex-row gap-4 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, code, or category..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
                <SelectItem value="on-hold">On Hold</SelectItem>
              </SelectContent>
            </Select>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-full sm:w-[200px]">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((category) => (
                  <SelectItem key={category} value={category}>
                    {category}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Results count */}
          <div className="text-sm text-muted-foreground mb-4">
            Showing {filteredData.length} of {MOCK_SUPPLIERS.length} suppliers
          </div>

          {/* Data Table */}
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Supplier</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-center">Performance</TableHead>
                  <TableHead className="text-right">Outstanding</TableHead>
                  <TableHead className="text-center">Orders</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      No suppliers found matching your criteria
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredData.map((supplier) => {
                    const primaryContact = getPrimaryContact(supplier);
                    
                    return (
                      <TableRow 
                        key={supplier.id} 
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => handleRowClick(supplier)}
                      >
                        <TableCell>
                          <div>
                            <div className="font-medium">{supplier.name}</div>
                            <div className="text-sm text-muted-foreground font-mono">{supplier.code}</div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{supplier.category}</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <div className="font-medium">{primaryContact.name}</div>
                            <div className="text-muted-foreground">{primaryContact.email}</div>
                          </div>
                        </TableCell>
                        <TableCell>{getStatusBadge(supplier.status)}</TableCell>
                        <TableCell className="text-center">
                          <div className="flex flex-col items-center gap-1">
                            <div className="text-sm font-medium">
                              {supplier.performance.onTimeDeliveryRate.toFixed(1)}%
                            </div>
                            {getPerformanceBadge(supplier.performance.onTimeDeliveryRate)}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="font-mono font-medium">
                            {supplier.accountBalance > 0 ? (
                              <span className="text-blue-600">
                                {formatCurrency(supplier.accountBalance, supplier.currency)}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="outline">{supplier.performance.totalOrdersPlaced}</Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigateTo("purchase-orders");
                              }}
                            >
                              <ShoppingCart className="h-4 w-4 mr-1" />
                              Orders
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Detail Drawer */}
      <Sheet open={showDetailDrawer} onOpenChange={setShowDetailDrawer}>
        <SheetContent className="w-full sm:max-w-3xl overflow-y-auto p-3">
          {selectedSupplier && (
            <>
              <SheetHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <SheetTitle>{selectedSupplier.name}</SheetTitle>
                    {getStatusBadge(selectedSupplier.status)}
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => toast.info("View in Access Financials")}
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    View in ERP
                  </Button>
                </div>
                <SheetDescription>
                  {selectedSupplier.code} • {selectedSupplier.category}
                </SheetDescription>
              </SheetHeader>

              <Tabs defaultValue="overview" className="mt-6">
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="overview">Overview</TabsTrigger>
                  <TabsTrigger value="contacts">Contacts</TabsTrigger>
                  <TabsTrigger value="performance">Performance</TabsTrigger>
                  <TabsTrigger value="orders">Orders</TabsTrigger>
                </TabsList>

                {/* Overview Tab */}
                <TabsContent value="overview" className="space-y-6">
                  {/* Sync Information */}
                  <div className="bg-muted/50 p-3 rounded-lg text-sm">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <RefreshCw className="h-4 w-4 text-muted-foreground" />
                        <span className="text-muted-foreground">Last synced:</span>
                        <span className="font-medium">{formatDateTime(selectedSupplier.lastSyncDate)}</span>
                      </div>
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                        {selectedSupplier.syncedFrom}
                      </Badge>
                    </div>
                  </div>

                  {/* Basic Information */}
                  <div>
                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                      <Building2 className="h-4 w-4" />
                      Company Information
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-muted-foreground">Supplier Code</Label>
                        <p className="font-medium font-mono">{selectedSupplier.code}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Legal Name</Label>
                        <p className="font-medium">{selectedSupplier.name}</p>
                      </div>
                      {selectedSupplier.tradingName && (
                        <div>
                          <Label className="text-muted-foreground">Trading Name</Label>
                          <p className="font-medium">{selectedSupplier.tradingName}</p>
                        </div>
                      )}
                      <div>
                        <Label className="text-muted-foreground">Category</Label>
                        <p className="font-medium">{selectedSupplier.category}</p>
                      </div>
                      {selectedSupplier.taxNumber && (
                        <div>
                          <Label className="text-muted-foreground">Tax Number</Label>
                          <p className="font-medium font-mono">{selectedSupplier.taxNumber}</p>
                        </div>
                      )}
                      {selectedSupplier.companyRegistration && (
                        <div>
                          <Label className="text-muted-foreground">Company Registration</Label>
                          <p className="font-medium font-mono">{selectedSupplier.companyRegistration}</p>
                        </div>
                      )}
                      {selectedSupplier.website && (
                        <div className="col-span-2">
                          <Label className="text-muted-foreground">Website</Label>
                          <a 
                            href={selectedSupplier.website} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="font-medium text-primary hover:underline flex items-center gap-1"
                          >
                            <Globe className="h-3 w-3" />
                            {selectedSupplier.website}
                          </a>
                        </div>
                      )}
                      <div>
                        <Label className="text-muted-foreground">Member Since</Label>
                        <p className="font-medium">{formatDate(selectedSupplier.createdDate)}</p>
                      </div>
                    </div>
                  </div>

                  {/* Financial Information */}
                  <div>
                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                      <DollarSign className="h-4 w-4" />
                      Financial Terms
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-muted-foreground">Payment Terms</Label>
                        <p className="font-medium">{selectedSupplier.paymentTerms}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Currency</Label>
                        <p className="font-medium font-mono">{selectedSupplier.currency}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Credit Limit</Label>
                        <p className="font-medium text-lg">
                          {formatCurrency(selectedSupplier.creditLimit, selectedSupplier.currency)}
                        </p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Current Outstanding</Label>
                        <p className="font-medium text-lg text-blue-600">
                          {formatCurrency(selectedSupplier.accountBalance, selectedSupplier.currency)}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Addresses */}
                  <div>
                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                      <MapPin className="h-4 w-4" />
                      Addresses
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {selectedSupplier.addresses.map((address, idx) => (
                        <div key={idx} className="bg-muted/50 p-3 rounded-lg">
                          <div className="font-medium mb-2 capitalize">{address.type} Address</div>
                          <div className="text-sm space-y-0.5">
                            <p>{address.line1}</p>
                            {address.line2 && <p>{address.line2}</p>}
                            <p>{address.city}</p>
                            {address.county && <p>{address.county}</p>}
                            <p>{address.postcode}</p>
                            <p>{address.country}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Notes */}
                  {selectedSupplier.notes && (
                    <div>
                      <h3 className="font-semibold mb-3">Notes</h3>
                      <p className="text-sm bg-muted p-3 rounded-md">{selectedSupplier.notes}</p>
                    </div>
                  )}
                </TabsContent>

                {/* Contacts Tab */}
                <TabsContent value="contacts" className="space-y-4">
                  {selectedSupplier.contacts.map((contact, idx) => (
                    <Card key={idx}>
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <div>
                            <CardTitle className="text-lg">{contact.name}</CardTitle>
                            <CardDescription>{contact.role}</CardDescription>
                          </div>
                          {contact.isPrimary && (
                            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                              Primary Contact
                            </Badge>
                          )}
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4 text-muted-foreground" />
                          <a href={`mailto:${contact.email}`} className="text-primary hover:underline">
                            {contact.email}
                          </a>
                        </div>
                        <div className="flex items-center gap-2">
                          <Phone className="h-4 w-4 text-muted-foreground" />
                          <a href={`tel:${contact.phone}`} className="text-primary hover:underline">
                            {contact.phone}
                          </a>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </TabsContent>

                {/* Performance Tab */}
                <TabsContent value="performance" className="space-y-6">
                  {/* Performance Metrics */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Card>
                      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">On-Time Delivery</CardTitle>
                        <Clock className="h-4 w-4 text-muted-foreground" />
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">
                          {selectedSupplier.performance.onTimeDeliveryRate.toFixed(1)}%
                        </div>
                        <div className="mt-2">
                          {getPerformanceBadge(selectedSupplier.performance.onTimeDeliveryRate)}
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Quality Rating</CardTitle>
                        <Star className="h-4 w-4 text-yellow-400" />
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">
                          {selectedSupplier.performance.qualityRating.toFixed(1)}/5.0
                        </div>
                        <div className="mt-2">
                          {renderStarRating(selectedSupplier.performance.qualityRating)}
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Avg Lead Time</CardTitle>
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">
                          {selectedSupplier.performance.averageLeadTimeDays}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          days
                        </p>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Total Orders</CardTitle>
                        <Package className="h-4 w-4 text-muted-foreground" />
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">
                          {selectedSupplier.performance.totalOrdersPlaced}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          purchase orders
                        </p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Order Value */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm font-medium">Total Order Value</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-blue-600">
                        {formatCurrency(selectedSupplier.performance.totalOrderValue, selectedSupplier.currency)}
                      </div>
                      <p className="text-sm text-muted-foreground mt-2">
                        Last order: {formatDate(selectedSupplier.performance.lastOrderDate)}
                      </p>
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Orders Tab */}
                <TabsContent value="orders" className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold">Recent Purchase Orders</h3>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => navigateTo("purchase-orders")}
                    >
                      View All Orders
                    </Button>
                  </div>

                  {selectedSupplier.recentPurchaseOrders.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>No recent purchase orders</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {selectedSupplier.recentPurchaseOrders.map((po) => (
                        <Card key={po.id}>
                          <CardContent className="pt-6">
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="font-mono font-bold text-lg">{po.poNumber}</div>
                                <div className="text-sm text-muted-foreground mt-1">
                                  Ordered: {formatDate(po.orderDate)}
                                </div>
                                <div className="text-sm text-muted-foreground">
                                  Expected: {formatDate(po.expectedDeliveryDate)}
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="font-bold text-xl mb-2">
                                  {formatCurrency(po.totalValue, po.currency)}
                                </div>
                                {getStatusBadge(po.status)}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </TabsContent>
              </Tabs>

              {/* Actions */}
              <div className="flex gap-2 pt-6 border-t mt-6">
                <Button 
                  className="flex-1"
                  onClick={() => {
                    navigateTo("purchase-orders");
                    setShowDetailDrawer(false);
                  }}
                >
                  <ShoppingCart className="h-4 w-4 mr-2" />
                  View Purchase Orders
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => toast.info("View in Access Financials")}
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  View in ERP
                </Button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Quick Add Supplier Drawer */}
      <Sheet open={showQuickAddDrawer} onOpenChange={setShowQuickAddDrawer}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <div className="flex items-center gap-2">
              <SheetTitle>Quick Add Supplier</SheetTitle>
              <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                <Zap className="h-3 w-3 mr-1" />
                Future Implementation
              </Badge>
            </div>
            <SheetDescription>
              This will be an integrated micro frontend for quickly adding suppliers
            </SheetDescription>
          </SheetHeader>

          <div className="mt-6 space-y-6">
            {/* Vision Card */}
            <Card className="border-purple-200 bg-purple-50/50">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Zap className="h-5 w-5 text-purple-600" />
                  Micro Frontend Integration
                </CardTitle>
                <CardDescription>
                  This feature will be powered by a dedicated micro frontend application
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="rounded-full bg-purple-100 p-2 mt-0.5">
                      <Building2 className="h-4 w-4 text-purple-600" />
                    </div>
                    <div>
                      <div className="font-medium">Standalone Application</div>
                      <div className="text-sm text-muted-foreground">
                        Independent micro frontend that can be developed and deployed separately
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="rounded-full bg-purple-100 p-2 mt-0.5">
                      <Zap className="h-4 w-4 text-purple-600" />
                    </div>
                    <div>
                      <div className="font-medium">Quick Data Entry</div>
                      <div className="text-sm text-muted-foreground">
                        Streamlined form for rapid supplier onboarding with smart defaults
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="rounded-full bg-purple-100 p-2 mt-0.5">
                      <RefreshCw className="h-4 w-4 text-purple-600" />
                    </div>
                    <div>
                      <div className="font-medium">Real-time Sync</div>
                      <div className="text-sm text-muted-foreground">
                        Automatically syncs with Access Financials and updates the main supplier list
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="rounded-full bg-purple-100 p-2 mt-0.5">
                      <CheckCircle className="h-4 w-4 text-purple-600" />
                    </div>
                    <div>
                      <div className="font-medium">Validation & Verification</div>
                      <div className="text-sm text-muted-foreground">
                        Built-in validation for tax numbers, company registration, and duplicate detection
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Preview of Future Form */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Form Preview</CardTitle>
                <CardDescription>
                  A glimpse of the quick add interface (non-functional)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 opacity-60 pointer-events-none">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Supplier Name *</Label>
                    <Input placeholder="Enter supplier name" disabled />
                  </div>
                  <div className="space-y-2">
                    <Label>Supplier Code</Label>
                    <Input placeholder="Auto-generated" disabled />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Category *</Label>
                    <Select disabled>
                      <SelectTrigger>
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Tax Number</Label>
                    <Input placeholder="e.g., GB123456789" disabled />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Primary Contact Email *</Label>
                  <Input type="email" placeholder="contact@supplier.com" disabled />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Phone Number</Label>
                    <Input placeholder="+44 20 1234 5678" disabled />
                  </div>
                  <div className="space-y-2">
                    <Label>Payment Terms</Label>
                    <Select disabled>
                      <SelectTrigger>
                        <SelectValue placeholder="Select terms" />
                      </SelectTrigger>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Address</Label>
                  <Input placeholder="Street address" disabled className="mb-2" />
                  <div className="grid grid-cols-3 gap-2">
                    <Input placeholder="City" disabled />
                    <Input placeholder="Postcode" disabled />
                    <Input placeholder="Country" disabled />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Technical Details */}
            <Card className="border-blue-200 bg-blue-50/50">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="h-4 w-4 text-blue-600" />
                  Technical Architecture
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-blue-600"></div>
                    <span className="font-medium">Module Federation:</span>
                    <span className="text-muted-foreground">Webpack 5 Module Federation for runtime integration</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-blue-600"></div>
                    <span className="font-medium">Independent Deployment:</span>
                    <span className="text-muted-foreground">Can be updated without redeploying main app</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-blue-600"></div>
                    <span className="font-medium">Shared Context:</span>
                    <span className="text-muted-foreground">Access to authentication and integration status</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-blue-600"></div>
                    <span className="font-medium">Event Communication:</span>
                    <span className="text-muted-foreground">Publishes events when suppliers are added</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Call to Action */}
            <div className="bg-muted p-4 rounded-lg text-center">
              <p className="text-sm text-muted-foreground mb-3">
                This feature is part of the roadmap for enhanced supplier management
              </p>
              <Button variant="outline" disabled>
                <Building2 className="h-4 w-4 mr-2" />
                Coming Soon
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Help Drawer */}
      <ContextualHelpDrawer
        open={isHelpOpen}
        onOpenChange={setIsHelpOpen}
        helpContent={helpContent}
      />
    </div>
  );
}
